import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { toPng } from "html-to-image";
import {
  ArrowLeft, Plus, Trash2, Zap, Loader2, Share2, RefreshCw, Trophy,
  Edit2, Check, X as XIcon,
} from "lucide-react";
import Bracket from "../components/Bracket";
import { useAuth } from "../lib/auth";
import { generate, applyScore } from "../lib/bracketGen";

const STORAGE_KEY = "spicy_bracket_gen_v1";

const FORMATS = [
  { value: "single_elimination", label: "Single Elimination", desc: "Lose once, you're out. Classic tournament tree." },
  { value: "round_robin", label: "Round Robin", desc: "Everyone plays everyone. Top wins by record." },
  { value: "group_advancement", label: "Group Advancement", desc: "4-team groups, top 2 advance. Needs multiples of 4." },
];

function loadDraft(userId) {
  try {
    const raw = localStorage.getItem(`${STORAGE_KEY}:${userId}`);
    return raw ? JSON.parse(raw) : null;
  } catch (_) { return null; }
}

function saveDraft(userId, state) {
  try { localStorage.setItem(`${STORAGE_KEY}:${userId}`, JSON.stringify(state)); } catch (_) {}
}

function clearDraft(userId) {
  try { localStorage.removeItem(`${STORAGE_KEY}:${userId}`); } catch (_) {}
}

export default function BracketGeneratorPage() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  const [step, setStep] = useState(1); // 1=setup, 2=teams, 3=bracket
  const [name, setName] = useState("My Bracket");
  const [format, setFormat] = useState("single_elimination");
  const [teams, setTeams] = useState([]);
  const [matches, setMatches] = useState([]);
  const [activeMatch, setActiveMatch] = useState(null);
  const bulkRef = useRef(null);
  const bracketBoxRef = useRef(null);

  // Restore draft after auth resolves
  useEffect(() => {
    if (loading || !user) return;
    const d = loadDraft(user.id);
    if (d) {
      setName(d.name || "My Bracket");
      setFormat(d.format || "single_elimination");
      setTeams(d.teams || []);
      setMatches(d.matches || []);
      if ((d.matches || []).length > 0) setStep(3);
      else if ((d.teams || []).length > 0) setStep(2);
    }
  }, [user, loading]);

  // Persist on every change
  useEffect(() => {
    if (!user) return;
    saveDraft(user.id, { name, format, teams, matches });
  }, [user, name, format, teams, matches]);

  const teamsById = useMemo(() => Object.fromEntries(teams.map((t) => [t.id, t])), [teams]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-zinc-400">Loading...</div>;
  }
  if (!user) {
    // Guard — bounce to home with a toast (App-level route guard would be cleaner but this keeps changes local)
    navigate("/", { replace: true });
    return null;
  }

  const addTeam = (rawName) => {
    const n = (rawName || "").trim();
    if (!n) return;
    if (teams.length >= 64) { toast.error("Max 64 teams"); return; }
    if (teams.some((t) => t.name.toLowerCase() === n.toLowerCase())) {
      toast.error(`"${n}" already added`);
      return;
    }
    const COLORS = ["#E60000", "#FF6B35", "#FFD23F", "#06A77D", "#005377", "#7B2CBF", "#FF3CAC", "#3D348B"];
    setTeams((prev) => [
      ...prev,
      {
        id: (typeof crypto !== "undefined" && crypto.randomUUID)
          ? crypto.randomUUID()
          : Math.random().toString(36).slice(2),
        name: n,
        seed: prev.length + 1,
        color: COLORS[prev.length % COLORS.length],
      },
    ]);
  };

  const bulkAdd = () => {
    const raw = (bulkRef.current?.value || "").trim();
    if (!raw) return;
    const names = raw.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
    let added = 0;
    names.forEach((n) => {
      if (teams.length + added >= 64) return;
      if (teams.some((t) => t.name.toLowerCase() === n.toLowerCase())) return;
      addTeam(n);
      added++;
    });
    if (bulkRef.current) bulkRef.current.value = "";
    toast.success(`Added ${added} team${added === 1 ? "" : "s"}`);
  };

  const removeTeam = (id) => setTeams((prev) => prev.filter((t) => t.id !== id));
  const renameTeam = (id, newName) => {
    const n = (newName || "").trim();
    if (!n) return;
    setTeams((prev) => prev.map((t) => (t.id === id ? { ...t, name: n } : t)));
  };

  const generateBracket = () => {
    try {
      const ms = generate(format, teams);
      setMatches(ms);
      setStep(3);
      toast.success("Bracket generated");
    } catch (e) {
      toast.error(e.message || "Could not generate bracket");
    }
  };

  const reset = () => {
    if (!window.confirm("Reset and start over? Your current bracket will be cleared.")) return;
    setName("My Bracket");
    setFormat("single_elimination");
    setTeams([]);
    setMatches([]);
    setActiveMatch(null);
    setStep(1);
    clearDraft(user.id);
  };

  const onMatchClick = (m) => setActiveMatch(m);

  const saveScore = (scoreA, scoreB) => {
    if (!activeMatch) return;
    setMatches((prev) => applyScore(prev, activeMatch.id, scoreA, scoreB));
    setActiveMatch(null);
  };

  const downloadPng = async () => {
    if (!bracketBoxRef.current) return;
    const t = toast.loading("Generating image...");
    try {
      const dataUrl = await toPng(bracketBoxRef.current, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: "#070303",
      });
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `${(name || "bracket").replace(/[^a-z0-9]+/gi, "_")}.png`;
      a.click();
      toast.success("Downloaded", { id: t });
    } catch (e) {
      toast.error("Image export failed", { id: t });
    }
  };

  return (
    <div className="min-h-screen pb-20" data-testid="bracket-generator-page">
      {/* Header */}
      <div className="border-b border-white/5 bg-black/40 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="btn-ghost-glass !py-1.5 !px-3 !text-xs"
            data-testid="bg-back-btn"
          >
            <ArrowLeft size={14} /> Back
          </button>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.3em] text-red-400 font-mono">
              Bracket generator
            </div>
            <h1 className="font-display font-black text-xl md:text-2xl truncate">{name}</h1>
          </div>
          {step === 3 && (
            <>
              <button
                onClick={downloadPng}
                className="btn-ghost-glass !py-1.5 !px-3 !text-xs"
                data-testid="bg-download-btn"
              >
                <Share2 size={14} /> Share PNG
              </button>
              <button
                onClick={reset}
                className="btn-ghost-glass !py-1.5 !px-3 !text-xs !text-red-300 !border-red-500/30"
                data-testid="bg-reset-btn"
              >
                <RefreshCw size={14} /> Reset
              </button>
            </>
          )}
        </div>
        {/* Stepper */}
        <div className="max-w-7xl mx-auto px-6 pb-3 flex items-center gap-2 text-[11px] font-mono">
          {[1, 2, 3].map((n) => (
            <React.Fragment key={n}>
              <button
                onClick={() => {
                  if (n === 1) setStep(1);
                  if (n === 2 && teams.length === 0) return;
                  if (n === 2) setStep(2);
                  if (n === 3 && matches.length > 0) setStep(3);
                }}
                disabled={(n === 2 && teams.length === 0) || (n === 3 && matches.length === 0)}
                className={`px-2.5 py-1 rounded-full border tracking-widest uppercase transition-colors ${
                  step === n
                    ? "bg-red-600 text-white border-red-500"
                    : "bg-white/5 text-zinc-400 border-white/10 hover:text-white disabled:opacity-40"
                }`}
                data-testid={`bg-step-${n}`}
              >
                {n}. {n === 1 ? "Setup" : n === 2 ? "Teams" : "Bracket"}
              </button>
              {n < 3 && <span className="text-zinc-700">→</span>}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Step 1: setup */}
      {step === 1 && (
        <section className="max-w-3xl mx-auto px-6 mt-10 space-y-6">
          <div className="glass-dark rounded-2xl p-6 border border-white/5">
            <label className="block mb-4">
              <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Bracket name</span>
              <input
                value={name}
                onChange={(e) => setName(e.target.value.slice(0, 60))}
                placeholder="Friday Night Cup"
                data-testid="bg-name-input"
                className="mt-1 w-full bg-black/50 border border-white/10 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-500"
              />
            </label>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-2">Format</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {FORMATS.map((f) => (
                <button
                  key={f.value}
                  onClick={() => setFormat(f.value)}
                  data-testid={`bg-format-${f.value}`}
                  className={`text-left rounded-xl p-3 border transition-colors ${
                    format === f.value
                      ? "border-red-500/60 bg-red-500/10"
                      : "border-white/10 bg-white/[0.02] hover:border-white/20"
                  }`}
                >
                  <div className="text-sm font-semibold text-white">{f.label}</div>
                  <div className="text-[11px] text-zinc-500 font-mono mt-1 leading-snug">{f.desc}</div>
                </button>
              ))}
            </div>
          </div>
          <button
            onClick={() => setStep(2)}
            disabled={!name.trim()}
            data-testid="bg-next-1"
            className="btn-liquid w-full justify-center !py-3 disabled:opacity-40"
          >
            <Zap size={14} /> Next: add teams
          </button>
        </section>
      )}

      {/* Step 2: teams */}
      {step === 2 && (
        <section className="max-w-3xl mx-auto px-6 mt-10 space-y-4">
          <div className="glass-dark rounded-2xl p-6 border border-white/5">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-2">
              Add teams · {teams.length} / 64
            </div>
            <div className="flex gap-2 mb-4">
              <input
                type="text"
                placeholder="Type a team name and press Enter"
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.preventDefault(); addTeam(e.currentTarget.value); e.currentTarget.value = ""; }
                }}
                data-testid="bg-team-input"
                className="flex-1 bg-black/50 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-red-500"
              />
            </div>
            <details className="mb-4">
              <summary className="text-[11px] text-zinc-400 font-mono cursor-pointer hover:text-white">
                Paste a list (one per line or comma-separated)
              </summary>
              <textarea
                ref={bulkRef}
                rows={4}
                placeholder={"Team Alpha\nTeam Bravo\nTeam Charlie"}
                data-testid="bg-bulk-input"
                className="mt-2 w-full bg-black/50 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-red-500 font-mono resize-none"
              />
              <button
                onClick={bulkAdd}
                className="btn-ghost-glass !py-1.5 !px-3 !text-xs mt-2"
                data-testid="bg-bulk-add"
              >
                <Plus size={12} /> Bulk add
              </button>
            </details>

            <div className="space-y-1.5 max-h-[400px] overflow-y-auto">
              {teams.length === 0 ? (
                <div className="text-center text-zinc-600 text-sm py-8 font-mono">
                  No teams yet. Add some above.
                </div>
              ) : (
                teams.map((t, i) => (
                  <TeamRow
                    key={t.id}
                    team={t}
                    seed={i + 1}
                    onRemove={() => removeTeam(t.id)}
                    onRename={(name) => renameTeam(t.id, name)}
                  />
                ))
              )}
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => setStep(1)}
              className="btn-ghost-glass !py-2.5 !px-4"
              data-testid="bg-back-step-1"
            >
              <ArrowLeft size={14} /> Setup
            </button>
            <button
              onClick={generateBracket}
              disabled={teams.length < 2}
              data-testid="bg-generate-btn"
              className="btn-liquid flex-1 justify-center !py-2.5 disabled:opacity-40"
            >
              <Trophy size={14} /> Generate bracket ({teams.length} teams)
            </button>
          </div>
        </section>
      )}

      {/* Step 3: bracket */}
      {step === 3 && (
        <section className="max-w-7xl mx-auto px-6 mt-6">
          <div
            ref={bracketBoxRef}
            className="glass-dark rounded-2xl overflow-hidden bg-[#070303]"
          >
            {matches.length > 0 ? (
              <Bracket
                matches={matches}
                teamsById={teamsById}
                format={format}
                onMatchClick={onMatchClick}
              />
            ) : (
              <div className="text-center text-zinc-500 py-24">Bracket not yet generated.</div>
            )}
          </div>
          <p className="text-[11px] text-zinc-500 font-mono text-center mt-3">
            Tap any match to enter the score. Winners advance automatically (single elim).
          </p>
        </section>
      )}

      {/* Match score modal */}
      {activeMatch && (
        <ScoreModal
          match={activeMatch}
          teamsById={teamsById}
          onClose={() => setActiveMatch(null)}
          onSave={saveScore}
        />
      )}
    </div>
  );
}

function TeamRow({ team, seed, onRemove, onRename }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(team.name);
  return (
    <div className="flex items-center gap-2 rounded-lg px-3 py-2 bg-white/[0.02] border border-white/5">
      <span className="text-[10px] font-mono text-zinc-500 w-6 shrink-0">#{seed}</span>
      <div className="h-7 w-7 rounded-full shrink-0 flex items-center justify-center text-[10px] font-bold text-white" style={{ background: team.color }}>
        {(team.name || "?").slice(0, 2).toUpperCase()}
      </div>
      {editing ? (
        <input
          autoFocus
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onBlur={() => { onRename(val); setEditing(false); }}
          onKeyDown={(e) => {
            if (e.key === "Enter") { onRename(val); setEditing(false); }
            if (e.key === "Escape") { setVal(team.name); setEditing(false); }
          }}
          className="flex-1 bg-black/50 border border-red-500/40 rounded-md px-2 py-1 text-sm focus:outline-none"
        />
      ) : (
        <span className="flex-1 text-sm truncate font-medium">{team.name}</span>
      )}
      <button
        onClick={() => setEditing((e) => !e)}
        className="h-7 w-7 rounded-md flex items-center justify-center text-zinc-500 hover:text-white"
        title="Rename"
      >
        <Edit2 size={12} />
      </button>
      <button
        onClick={onRemove}
        className="h-7 w-7 rounded-md flex items-center justify-center text-zinc-500 hover:text-red-400"
        title="Remove"
        data-testid={`bg-remove-${team.id}`}
      >
        <Trash2 size={12} />
      </button>
    </div>
  );
}

function ScoreModal({ match, teamsById, onClose, onSave }) {
  const ta = teamsById[match.team_a_id];
  const tb = teamsById[match.team_b_id];
  const [a, setA] = useState(match.team_a_score ?? match.score_a ?? "");
  const [b, setB] = useState(match.team_b_score ?? match.score_b ?? "");
  if (!ta || !tb) {
    return (
      <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
        <div className="max-w-sm w-full bg-[#0a0303] border border-red-500/30 rounded-2xl p-6 text-center">
          <p className="text-sm text-zinc-300 mb-4">
            Both teams need to be confirmed before you can score this match.
          </p>
          <button onClick={onClose} className="btn-liquid w-full justify-center !py-2">Close</button>
        </div>
      </div>
    );
  }
  return (
    <div
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="max-w-sm w-full bg-[#0a0303] border border-red-500/30 rounded-2xl p-6 relative">
        <button onClick={onClose} className="absolute top-3 right-3 h-8 w-8 rounded-full flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/10">
          <XIcon size={16} />
        </button>
        <div className="text-[10px] uppercase tracking-widest text-red-400 font-mono mb-2">
          {match.round_name || `Round ${(match.round || 0) + 1}`}
        </div>
        <h3 className="font-display font-bold text-lg mb-5">Enter score</h3>
        <div className="space-y-3">
          <ScoreInput label={ta.name} color={ta.color} value={a} onChange={setA} />
          <div className="text-center text-zinc-600 text-xs">vs</div>
          <ScoreInput label={tb.name} color={tb.color} value={b} onChange={setB} />
        </div>
        <button
          onClick={() => onSave(a, b)}
          disabled={a === "" || b === "" || Number(a) === Number(b)}
          data-testid="bg-save-score"
          className="btn-liquid w-full justify-center !py-2.5 mt-5 disabled:opacity-40"
        >
          <Check size={14} /> Save score
        </button>
        {a !== "" && b !== "" && Number(a) === Number(b) && (
          <p className="text-[11px] text-amber-300 font-mono text-center mt-2">
            Ties aren't supported in elimination brackets.
          </p>
        )}
      </div>
    </div>
  );
}

function ScoreInput({ label, color, value, onChange }) {
  return (
    <div className="flex items-center gap-3 bg-white/[0.03] border border-white/10 rounded-lg p-3">
      <div className="h-8 w-8 rounded-full shrink-0" style={{ background: color }} />
      <span className="flex-1 font-medium truncate">{label}</span>
      <input
        type="number"
        min={0}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-16 bg-black/60 border border-white/10 rounded-md px-2 py-1.5 text-right font-mono font-bold text-lg focus:outline-none focus:border-red-500"
      />
    </div>
  );
}
